import React, { useState, useEffect, useContext } from 'react';
import { useNavigateToDetail } from '../../hooks/useNavigateToDetail';
import toast from 'react-hot-toast';
import { createActivity, updateActivity } from '../../services/calendarApi';
import PermissionGuard from '../Common/PermissionGuard';
import AuthContext from '../../contexts/AuthContext';
import { useReminder } from '../../contexts/ReminderContext';
import { useLocation } from '../../contexts/LocationContext';
import crmApi from '../../services/crmApi';

const ActivityModal = ({ activity, onClose, onSave, onDelete, onMarkComplete }) => {
  const navigateToDetail = useNavigateToDetail();
  const { user } = useContext(AuthContext);
  const { refreshActivities } = useReminder();
  const { currentLocation } = useLocation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'task',
    status: 'scheduled',
    priority: 'medium',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    location: '',
    notes: '',
    reminderEnabled: false,
    reminderMinutes: 15
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [hasLeadAccess, setHasLeadAccess] = useState(false);

  useEffect(() => {
    if (activity) {
      const startDate = new Date(activity.startDate);
      const endDate = new Date(activity.endDate);

      // For all-day events, use local date with 00:00 and 23:59 times
      const startTime = activity.allDay ? '00:00' : startDate.toTimeString().slice(0, 5);
      const endTime = activity.allDay ? '23:59' : endDate.toTimeString().slice(0, 5);

      // Use local date for the date picker (YYYY-MM-DD in local timezone)
      const localStartDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const localEndDate = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      setFormData({
        title: activity.title || '',
        description: activity.description || '',
        type: activity.type || 'task',
        status: activity.status || 'scheduled',
        priority: activity.priority || 'medium',
        startDate: localStartDate,
        startTime: startTime,
        endDate: localEndDate,
        endTime: endTime,
        allDay: activity.allDay || false,
        location: activity.location || '',
        notes: activity.notes || '',
        reminderEnabled: activity.reminder?.enabled || false,
        reminderMinutes: activity.reminder?.minutes || 15
      });

      // If activity has relatedTo data, user already has access
      // (backend filters activities by location/permissions)
      if (activity.relatedTo?.type === 'lead' && activity.relatedTo?.id) {
        setHasLeadAccess(true);
      }
    }
  }, [activity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    const isLeadActivity = activity?.relatedTo?.type === 'lead';

    let activityData = {
      title: formData.title,
      description: formData.description,
      type: formData.type,
      status: formData.status,
      priority: formData.priority,
      location: formData.location,
      locationId: currentLocation?._id || currentLocation?.id,
      notes: formData.notes,
      reminder: {
        // Lead activities always have reminders enabled
        enabled: isLeadActivity ? true : formData.reminderEnabled,
        minutes: formData.reminderMinutes
      }
    };

    // Combine date and time in the user's LOCAL timezone, then convert to a true UTC
    // ISO string. Applied to lead activities too, so their scheduled date/time is
    // editable here and synced back to the lead via the calendar update endpoint.
    const [startYear, startMonth, startDay] = formData.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = formData.endDate.split('-').map(Number);
    const [startHour, startMinute] = formData.startTime.split(':').map(Number);
    const [endHour, endMinute] = formData.endTime.split(':').map(Number);

    const startDateTime = new Date(startYear, startMonth - 1, startDay, startHour, startMinute, 0);
    const endDateTime = new Date(endYear, endMonth - 1, endDay, endHour, endMinute, 0);

    if (endDateTime <= startDateTime) {
      toast.error('End time must be after start time');
      return;
    }

    activityData = {
      ...activityData,
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      allDay: formData.allDay
    };

    setLoading(true);
    try {
      if (activity?._id) {
        await updateActivity(activity._id, activityData);
        toast.success('Activity updated successfully');
      } else {
        await createActivity(activityData);
        toast.success('Activity created successfully');
      }
      // Force refresh reminder context to get updated activity data
      if (refreshActivities) {
        await refreshActivities();
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save activity');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(activity._id);
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      await onMarkComplete(activity._id);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">
            {activity?._id ? 'Edit Activity' : 'New Activity'}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Lead Information - Show if activity is related to a lead */}
          {activity?.relatedTo?.type === 'lead' && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <h4 className="text-sm font-semibold text-blue-900">Related to Lead</h4>
                  </div>
                  <p className="text-sm text-blue-800 font-medium mb-1">{activity.relatedTo.name}</p>
                  <p className="text-xs text-blue-600">This activity was created from a lead interaction</p>
                </div>
                {hasLeadAccess && (
                  <PermissionGuard permission="CRM:LEADS:READ">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigateToDetail(`/crm/my-leads/${activity.relatedTo.id}`, 'calendar');
                      }}
                      className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Lead
                    </button>
                  </PermissionGuard>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter activity title"
              required
            />
          </div>

          {/* Type, Status, Priority */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="meeting">Meeting</option>
                <option value="task">Task</option>
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="event">Event</option>
                <option value="reminder">Reminder</option>
                <option value="note">Note</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Start Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={formData.allDay}
              />
            </div>
          </div>

          {/* End Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={formData.allDay}
              />
            </div>
          </div>

          {/* All Day */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.allDay}
              onChange={(e) => handleInputChange('allDay', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label className="ml-2 text-sm text-gray-700">All day event</label>
          </div>

          {/* Reminder Section */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Reminder
                {activity?.relatedTo?.type === 'lead' && (
                  <span className="ml-2 text-xs text-blue-600">(Enabled by default for lead activities)</span>
                )}
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.reminderEnabled}
                  onChange={(e) => handleInputChange('reminderEnabled', e.target.checked)}
                  disabled={activity?.relatedTo?.type === 'lead'}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label className="ml-2 text-sm text-gray-700">Enable reminder</label>
              </div>
            </div>
            
            {formData.reminderEnabled && (
              <div className="ml-6 space-y-2">
                <label className="block text-xs text-gray-600">Remind me before:</label>
                <select
                  value={formData.reminderMinutes}
                  onChange={(e) => handleInputChange('reminderMinutes', parseInt(e.target.value))}
                  disabled={activity?.relatedTo?.type === 'lead'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={1440}>1 day</option>
                </select>
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter location"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Enter description"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Additional notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex gap-2">
              {activity?._id && (
                <>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting || loading || completing}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {deleting && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                  {activity.status !== 'completed' && (
                    <button
                      type="button"
                      onClick={handleMarkComplete}
                      disabled={completing || loading || deleting}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {completing && (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {completing ? 'Completing...' : 'Mark Complete'}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || deleting || completing}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityModal;
